/**
 * [QSCHED_UI] Quick Schedule Menu
 *
 * Dropdown lista os QuickMessages marcados como template de agendamento
 * (isScheduleTemplate=true) e permite agendar em 1 clique.
 *
 * Props:
 *   contactId, ticketId, whatsappId, onDone(schedule)
 */
// [QSCHED_FIX_AUTHCTX] passa companyId+userId no /quick-messages/list
import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../context/Auth/AuthContext";
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemText,
  Tooltip,
  Divider,
  Typography,
  CircularProgress,
  Box
} from "@material-ui/core";
import AlarmAddIcon from "@material-ui/icons/AlarmAdd";
import EventIcon from "@material-ui/icons/Event";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const humanizeOffset = (offset) => {
  if (!offset) return "";
  const s = String(offset).trim().toUpperCase();
  let m = s.match(/^TODAY_(\d{1,2}):(\d{2})$/);
  if (m) return `Hoje ${m[1].padStart(2, "0")}:${m[2]}`;
  m = s.match(/^TOMORROW_(\d{1,2}):(\d{2})$/);
  if (m) return `Amanhã ${m[1].padStart(2, "0")}:${m[2]}`;
  m = s.match(/^\+(\d{1,3})H$/);
  if (m) return `Em ${m[1]}h`;
  m = s.match(/^\+(\d{1,3})D_(\d{1,2}):(\d{2})$/);
  if (m) return `Em ${m[1]}d às ${m[2].padStart(2, "0")}:${m[3]}`;
  return offset;
};

const QuickScheduleMenu = ({ contactId, ticketId, whatsappId, onDone }) => {
  const { user } = useContext(AuthContext);
  const [anchorEl, setAnchorEl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const open = Boolean(anchorEl);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/quick-messages/list", {
        params: {
          companyId: user?.companyId,
          userId: user?.id,
          isOficial: "false",
          onlyRespostasRapidas: true
        }
      });
      const list = (data || []).filter(
        (q) => q.isScheduleTemplate === true && q.scheduleOffset
      );
      setTemplates(list);
    } catch (err) {
      console.error("[QSCHED] fetchTemplates", err);
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (e) => {
    setAnchorEl(e.currentTarget);
    fetchTemplates();
  };

  const handleClose = () => setAnchorEl(null);

  const handlePick = async (qm) => {
    if (submitting) return;
    if (!contactId) {
      toast.warn("Contato do ticket não identificado.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/schedules/quick", {
        quickMessageId: qm.id,
        contactId,
        ticketId,
        whatsappId
      });
      const when = new Date(data.sendAt);
      const pretty = when.toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit"
      });
      toast.success(`⏰ Agendado #${qm.shortcode} — ${pretty}`);
      handleClose();
      if (typeof onDone === "function") onDone(data);
    } catch (err) {
      toastError(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Tooltip title="Agendar rápido (templates)">
        <IconButton onClick={handleOpen} size="small">
          <AlarmAddIcon />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        keepMounted
      >
        <Box px={2} py={1} minWidth={280}>
          <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
            ⏰ Agendar rápido
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Selecione um template
          </Typography>
        </Box>
        <Divider />
        {loading && (
          <Box display="flex" justifyContent="center" py={2}>
            <CircularProgress size={20} />
          </Box>
        )}
        {!loading && templates.length === 0 && (
          <Box px={2} py={2}>
            <Typography variant="body2" color="textSecondary">
              Nenhum template de agendamento cadastrado.
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Cadastre em <strong>Respostas Rápidas</strong> marcando
              &quot;É agendamento?&quot;.
            </Typography>
          </Box>
        )}
        {!loading &&
          templates.map((qm) => (
            <MenuItem
              key={qm.id}
              onClick={() => handlePick(qm)}
              disabled={submitting}
            >
              <EventIcon
                fontSize="small"
                style={{ marginRight: 8, color: "#607d8b" }}
              />
              <ListItemText
                primary={
                  <span>
                    <strong>#{qm.shortcode}</strong>{" "}
                    <span
                      style={{
                        color: "#607d8b",
                        fontSize: "0.8em",
                        marginLeft: 4
                      }}
                    >
                      {humanizeOffset(qm.scheduleOffset)}
                    </span>
                  </span>
                }
                secondary={
                  qm.message
                    ? String(qm.message).slice(0, 60) +
                      (qm.message.length > 60 ? "…" : "")
                    : null
                }
              />
            </MenuItem>
          ))}
      </Menu>
    </>
  );
};

export default QuickScheduleMenu;
